import { Close, Delete } from '@mui/icons-material';
import {
    Box,
    Button,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Popper,
    Select,
    SelectChangeEvent,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRecoilState, useSetRecoilState } from 'recoil';
import ReviewStatus from '~/components/Structures/ReviewStatus';
import {
    AVAILABLE_ROI_TYPES,
    DICOMApprovalStatus,
    Roi,
    RoiInterpretedType,
} from '~/domain/dicom/dicomstructureset';
import useRoi from '~/hooks/structure-set/useRoi';
import useStructureSet from '~/hooks/structure-set/useStructureSet';
import {
    openedStructureDeletionConfirmationDialogState,
    selectedROIState,
} from '~/state/atoms/structureSet/roiAtoms';
import StructureNameField from './StructureField/NameField';
import StructureContourThicknessModifier from './StructureField/ThicknessModifier';

export type ROIPropertiesOverlayProps = {
    open: boolean;
    roi: Roi;
    anchorEl?: Element | null;
    onClose: () => void;
};

export default function ROIPropertiesOverlay({
    open,
    roi,
    anchorEl,
    onClose,
}: ROIPropertiesOverlayProps) {
    const { setStructureSetApprovalStatus } = useStructureSet();
    const [selectedROI, setSelectedROI] = useRecoilState(selectedROIState);
    const {
        roi: roiState,
        setName,
        setInterpretedType,
        setRoiApprovalStatus,
        setContourThickness,
        structureCanBeEdited,
        structureCanBeDeleted,
        getRoiVolumeCm3,
        getRoiEquivalentSphereDiameter,
    } = useRoi(roi);

    const setOpenedStructureDeletionConfirmationDialog = useSetRecoilState(
        openedStructureDeletionConfirmationDialogState
    );

    const handleInterpretedTypeChange = async (
        event: SelectChangeEvent<RoiInterpretedType>
    ) => {
        const roiNumber = selectedROI;
        setInterpretedType(event.target.value as RoiInterpretedType);
        // TODO: Remove this hack once state management uses immutable data.
        // This forces UI refresh and scrolls selected ROI into view after type change.
        await setSelectedROI(-1);
        setSelectedROI(roiNumber);
    };

    const allowedTypes = useMemo(() => {
        return Object.entries(AVAILABLE_ROI_TYPES).map(([key, value]) => (
            <MenuItem key={key} value={value}>
                {value}
            </MenuItem>
        ));
    }, []);

    const handleRoiReset = () => {
        setRoiApprovalStatus(DICOMApprovalStatus.UNAPPROVED);
        setStructureSetApprovalStatus(DICOMApprovalStatus.UNAPPROVED);
    };

    return (
        roiState && (
            <Popper
                open={open}
                anchorEl={anchorEl}
                placement="right-start"
                sx={{ zIndex: 1000 }}
                modifiers={[
                    {
                        name: 'offset',
                        options: {
                            offset: [-40, 16],
                        },
                    },
                ]}
            >
                <Box
                    width="650px"
                    bgcolor="background.paper"
                    boxShadow={3}
                    borderRadius={1}
                    p={2}
                >
                    <Box display="flex" justifyContent="space-between">
                        <FormattedMessage id="roiProperties" />
                        <IconButton size="small" onClick={onClose}>
                            <Close />
                        </IconButton>
                    </Box>
                    <Stack spacing={2}>
                        <Stack direction="row" spacing={1}>
                            <StructureNameField
                                roi={roiState}
                                onChange={setName}
                                onToggleEdit={() => {}}
                                label="Name"
                                variant="outlined"
                                disabled={!structureCanBeEdited}
                            />
                            <FormControl
                                size="small"
                                sx={{ width: 200, flexShrink: 0 }}
                            >
                                <InputLabel>Type</InputLabel>
                                <Select
                                    autoWidth
                                    label="Type"
                                    value={roiState.interpretedType}
                                    onChange={handleInterpretedTypeChange}
                                    disabled={!structureCanBeEdited}
                                    aria-disabled={!structureCanBeEdited}
                                    data-testid="roi-type-select"
                                    renderValue={(selected) => selected}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                width: 200,
                                                marginTop: 0.5,
                                            },
                                        },
                                    }}
                                >
                                    {allowedTypes}
                                </Select>
                            </FormControl>
                            <Tooltip
                                title={
                                    structureCanBeDeleted
                                        ? 'Delete Structure'
                                        : 'Structure is approved'
                                }
                            >
                                <div>
                                    <IconButton
                                        color="primary"
                                        disabled={!structureCanBeDeleted}
                                        aria-label="Delete Structure"
                                        onClick={() =>
                                            setOpenedStructureDeletionConfirmationDialog(
                                                roiState.roiNumber
                                            )
                                        }
                                        data-testid="delete-structure-button"
                                    >
                                        <Delete />
                                    </IconButton>
                                </div>
                            </Tooltip>
                        </Stack>
                        <Box display="flex" gap={1}>
                            <Tooltip
                                title={<FormattedMessage id="readOnlyField" />}
                                placement="bottom-start"
                                PopperProps={{
                                    modifiers: [
                                        {
                                            name: 'offset',
                                            options: {
                                                offset: [0, -8],
                                            },
                                        },
                                    ],
                                }}
                            >
                                <TextField
                                    label="Structure Code"
                                    size="small"
                                    value={
                                        roiState.identificationCodeDisplay
                                            .length > 0
                                            ? roiState.identificationCodeDisplay
                                            : 'Not defined'
                                    }
                                    fullWidth
                                    disabled={true}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Tooltip>
                        </Box>
                        <Box display="flex" gap={1}>

                            <Tooltip
                                title={<FormattedMessage id="readOnlyField" />}
                                placement="bottom-start"
                                PopperProps={{
                                    modifiers: [
                                        {
                                            name: 'offset',
                                            options: {
                                                offset: [0, -8],
                                            },
                                        },
                                    ],
                                }}
                            >
                                <TextField
                                    label="Relative Electron Density"
                                    size="small"
                                    value={
                                        roiState.relativeElectronDensity ??
                                        'Not defined'
                                    }
                                    disabled={true}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        width: '70%',
                                    }}
                                />
                            </Tooltip>
                            <Tooltip
                                title={<FormattedMessage id="readOnlyField" />}
                                placement="bottom-start"
                                PopperProps={{
                                    modifiers: [
                                        {
                                            name: 'offset',
                                            options: {
                                                offset: [0, -8],
                                            },
                                        },
                                    ],
                                }}
                            >
                                <TextField
                                    label="Density Override"
                                    size="small"
                                    value={
                                        roiState.densityOverride ??
                                        'Not defined'
                                    }
                                    disabled={true}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        width: '30%',
                                    }}
                                />
                            </Tooltip>
                        </Box>

                        <Stack direction={'row'} spacing={3}>
                            <Typography variant="subtitle2" fontWeight={600}>
                                Volume (cm&sup3;)
                                <Typography
                                    variant="subtitle2"
                                    fontWeight={200}
                                >
                                    {getRoiVolumeCm3().toFixed(2)}
                                </Typography>
                            </Typography>
                            <Typography variant="subtitle2" fontWeight={600}>
                                Eq. Sphere Diameter (cm)
                                <Typography
                                    variant="subtitle2"
                                    fontWeight={200}
                                >
                                    {getRoiEquivalentSphereDiameter().toFixed(
                                        2
                                    )}
                                </Typography>
                            </Typography>

                            <Typography variant="subtitle2" fontWeight={600}>
                                Contour origin
                                <Typography
                                    variant="subtitle2"
                                    fontWeight={200}
                                >
                                    {roi.roiGenerationAlgorithm}
                                </Typography>
                            </Typography>

                            <Typography variant="subtitle2" fontWeight={600}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    Review status
                                    {!structureCanBeEdited && (
                                        <Button
                                            sx={{
                                                pt: 0,
                                                pb: 0,
                                                fontSize: 10,
                                                minWidth: 0,
                                            }}
                                            onClick={() => handleRoiReset()}
                                            variant="text"
                                            color="primary"
                                            size="small"
                                        >
                                            <FormattedMessage id="reset" />
                                        </Button>
                                    )}
                                </Box>
                                <ReviewStatus status={roi.approvalStatus} />
                            </Typography>
                        </Stack>
                        <Box>
                            <StructureContourThicknessModifier
                                roi={roiState}
                                onContourThicknessChange={setContourThickness}
                            />
                        </Box>
                    </Stack>
                </Box>
            </Popper>
        )
    );
}
